import { SpellCheckService } from './../spell-check/spell-check.service';
import { DocumentScrollingService } from '../document/document-scrolling.service';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { defaultLanguage } from 'src/app/utils/directives/root-lang.directive';
import { FroalaInstanceManager, FroalaEditorAPI } from './interfaces';
import { DocumentMouseHandlerService } from '../document/document-mouse-handler.service';
import { BehaviorSubject, fromEvent, asyncScheduler, Subscription } from 'rxjs';
import { ReadingMode, ReadingEcho, ReadingUnitType } from 'src/app/core/reading-utils/enums';
import { SettingsService } from '../settings/settings.service';
import { debounceTime, distinctUntilChanged, throttleTime } from 'rxjs/operators';
import { DocumentKeyboardHandlerService } from '../document/document-keyboard-handler.service';
import { DocumentHighlightService } from '../document/document-highlight.service';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { AsgardDocFroala } from 'src/app/core/documents/asgard-doc-froala';
import { DocumentManagerService } from '../document/document-manager.service';
import { ReadingService } from '../text/reading.service';
import { PaneService } from '../pane.service';
import { ReadingModesService } from '../reading-modes.service';
import { matchReadingEchoWithSettings } from 'src/app/core/common';
import { ChainResponsibilityService } from '../chain-responsibility/chain-responsibility.service';
import { ChainSenders } from '../chain-responsibility/chain.interface';
import { CommandInvokerService } from '../command/command-invoker.service';
import { HTMLDocumentChangedCommand } from '../command/html-commands/document-changed.command';
import { AudioMakerService } from '../audio-maker/audio-maker.service';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';
import { isSymbolSpace } from 'src/app/core/common';
import { detectHotkey, getHotkeyString } from 'src/app/utils/helpers/hotkey-combination.helper';
import { HotkeyEventSource, HotkeysService } from '../exam/hotkeys.service';
import { CurrentExamService } from '../exam/current-exam.service';

interface HtmlEditorInstance {
  asgardDoc: AsgardDocHtml;
  editorAPI: FroalaEditorAPI | null;
  editorListeners: { type: string, listener: (...args) => void }[] | null;
  windowListeners: { type: string, listener: (...args) => void }[] | null;
  instSubscr: Subscription | null;
  validateClick: mouseEvt | null;
  paneIndex: number;
  froalaInstanceManager: FroalaInstanceManager | null;
  instanceReady: boolean;
}

type mouseEvt = (evt: MouseEvent) => { skip: boolean, rebuildTxtMap: boolean };

@Injectable({
  providedIn: 'root'
})
export class HtmlInstanceManagerService {
  private readonly instanceStorage: Map<string, HtmlEditorInstance> = new Map();

  private rwtTimerID: number | null = null;
  private mouseIn: boolean = false;
  private mouseHoverText: BehaviorSubject<MouseEvent | null> = new BehaviorSubject(null);
  private hasReadLastItem: boolean = false;

  // for the ui language of editor
  private currentUIEditorLanguage;

  constructor(private readonly docMouseService: DocumentMouseHandlerService,
    private readonly docKeyBoardService: DocumentKeyboardHandlerService,
    private readonly docHighlightSvc: DocumentHighlightService,
    private readonly documentScrollingService: DocumentScrollingService,
    private readonly docMgrService: DocumentManagerService,
    private readonly settingsService: SettingsService,
    private readonly readingService: ReadingService,
    private readonly spellCheckService: SpellCheckService,
    private readonly paneService: PaneService,
    private readonly readingModeService: ReadingModesService,
    private readonly chain: ChainResponsibilityService,
    private readonly commandInvoker: CommandInvokerService,
    private readonly audioMakerService: AudioMakerService,
    private readonly autosave: AutosaveManagerService,
    private readonly examService: CurrentExamService,
    private readonly hotkeyService: HotkeysService
  ) {
    this.initSubscriptions();
  }

  createEditorInstace(doc: AsgardDocHtml, instanceManager: FroalaInstanceManager | null = null,
    clkFltr: mouseEvt | null = null, paneIndex: number = 0): void {
    this.instanceStorage.set(doc.documentId,
      {
        editorAPI: null,
        instSubscr: null,
        editorListeners: null,
        windowListeners: null,
        asgardDoc: doc,
        validateClick: clkFltr,
        paneIndex,
        froalaInstanceManager: instanceManager,
        instanceReady: false
      });

    if (instanceManager !== null) {
      const htmlEditorInst = this.instanceStorage.get(doc.documentId);
      instanceManager.initialize();
      if (htmlEditorInst) {
        htmlEditorInst.editorAPI = instanceManager.getEditor();
      }
    } else {
      this.onInstanceReady(doc.documentId);
    }
  }

  getFroalaOptions(documentId: string) {
    this.currentUIEditorLanguage = defaultLanguage;
    return {
      iframe: true,
      iframeStyleFiles: ['/styles/froala-custom-styles.css'],
      spellcheck: false,
      height: 'calc(100% - 58px)',
      key: environment.froalaLicenceKey,
      attribution: false,
      autofocus: true,
      language: defaultLanguage,
      events: {
        initialized: this.onInstanceReady.bind(this, documentId),
        'image.inserted': ($img, _) => {
          this.replaceImgBlobToBase64($img[0] as HTMLImageElement);
        }
      },
      toolbarButtons: [
        ['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript'],
        ['fontFamily', 'fontSize', 'textColor', 'backgroundColor'],
        ['formatOLSimple', 'formatUL'],
        ['insertImage']
      ],
      toolbarButtonsMD: [
        ['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript'],
        ['fontFamily', 'fontSize', 'textColor', 'backgroundColor'],
        ['insertImage']
      ],
      toolbarButtonsSM: [
        ['fontFamily', 'fontSize', 'textColor', 'backgroundColor']
      ],
      toolbarButtonsXS: [
        ['fontFamily', 'fontSize']
      ],
      pluginsEnabled: [
        'align', 'colors', 'file', 'fontFamily', 'fontSize', 'save', 'lists', 'print', 'wordPaste', 'draggable', 'image'
      ],
      dragInline: true,
      charCounterCount: true,
      wordPasteModal: false,
      fontFamily: {
        Arial: 'Arial',
        Courier: 'Courier',
        Georgia: 'Georgia',
        Helvetica: 'Helvetica',
        Impact: 'Impact',
        OpenDyslexic: 'Open Dyslexic',
        TimesNewRoman: 'Times New Roman',
        Verdana: 'Verdana'
      },
      fontFamilyDefaultSelection: 'Arial',
      fontFamilySelection: true,
      imageEditButtons: ['imageAlign', 'imageDisplay', 'imageRemove']
    };
  }

  cleanOndestroy(): void {
    this.instanceStorage.forEach((_value, key) => this.destroy(key));
    this.instanceStorage.clear();
  }

  private destroy(documentId: string | null): void {
    if (documentId !== null) {
      const htmlEditorInst = this.instanceStorage.get(documentId);
      if (!htmlEditorInst) {
        return;
      }

      if (htmlEditorInst.instSubscr) {
        if (htmlEditorInst.editorListeners) {
          htmlEditorInst.editorListeners.forEach(x => {
            if (htmlEditorInst.asgardDoc.editorElement) {
              htmlEditorInst.asgardDoc.editorElement.removeEventListener(x.type, x.listener);
            }
          });
        }

        if (htmlEditorInst.windowListeners) {
          htmlEditorInst.windowListeners.forEach(x => {
            window.removeEventListener(x.type, x.listener);
          });
        }

        htmlEditorInst.instSubscr.unsubscribe();
      }

      this.instanceStorage.delete(documentId);
      console.log('destroy called for', documentId);
    }
  }

  private initSubscriptions(): void {
    this.mouseHoverText.asObservable().pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe(
        (e: MouseEvent) => {
          if (e) {
            if (e.clientX > 0 && e.clientY > 0) {
              this.docMouseService.handleHoverEvent(e);
            }
          }
        }
      );

    this.docMgrService.documentRemoved.subscribe(x => {
      this.destroy(x);
      // console.log('html-instance-manager', x);
    });

    this.paneService.ChangePandeIndexForTabsEv.subscribe((changed: boolean) => {
      if (changed) {
        this.instanceStorage.forEach(e => {
          if (e.paneIndex === 1) {
            e.paneIndex = 0;
          }
        });
      }
    });

    this.settingsService.getSettingsObservable().subscribe((settings) => {
      if (settings.UILanguageValue !== this.currentUIEditorLanguage) {
        this.currentUIEditorLanguage = settings.UILanguageValue;
        this.destroyAndReInitializeAllEditorInstances();
      }
    });
  }

  private initPaneToggleSub(htmlEditorInst: HtmlEditorInstance) {
    this.paneService.paneToggled.subscribe((e: boolean) => {
      if (e) {
        setTimeout(() => {
          this.syncIFrame(htmlEditorInst);
        }, 0);
      }
    });
  }

  private initActiveTabSub(htmlEditorInst: HtmlEditorInstance) {
    this.docMgrService.currentDocumentChanged.subscribe((e: string) => {
      if (this.instanceStorage.has(e)) {
        setTimeout(() => {
          this.syncIFrame(htmlEditorInst);
        }, 0);
      }
    });
  }

  private syncIFrame(editorInstance: HtmlEditorInstance) {
    if (editorInstance.editorAPI) {
      editorInstance.editorAPI.size.syncIframe();
    }
  }

  private onInstanceReady(documentId: string): void {
    // console.log('onInstanceReady', documentId);
    const htmlEditorInst = this.instanceStorage.get(documentId);
    if (htmlEditorInst) {
      if (!htmlEditorInst.instanceReady) {
        this.docMgrService.setDocumentOptins(documentId, htmlEditorInst.editorAPI !== null ? [htmlEditorInst.editorAPI] : []);

        this.initPaneToggleSub(htmlEditorInst);
        this.initActiveTabSub(htmlEditorInst);
  
        htmlEditorInst.instSubscr = (
          htmlEditorInst.asgardDoc.readingItemObservable.subscribe(() => 
            this.docHighlightSvc.onReadingItemUpdated(documentId))
        );
      }

      this.addEventsListeners(htmlEditorInst, documentId);
      htmlEditorInst.instanceReady = true;
    }
  }

  private replaceImgBlobToBase64(img: HTMLImageElement) {
    const src = img.src;

    if (src?.startsWith('blob:')) {
      fetch(src)
        .then(res => res.blob())
        .then(async blob => {
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64 = reader.result as string;
            img.src = base64;
            await this.autosave.triggerAutoSave();
          };
          reader.readAsDataURL(blob);
        })
        .catch(err => {
          console.error('Error converting blob to base64:', err);
        });
    }
  }

  private addEventsListeners(htmlEditorInst: HtmlEditorInstance, documentId: string): void {
    const clickEv = (evt: MouseEvent) => {
      if (htmlEditorInst.validateClick === null || !htmlEditorInst.validateClick(evt).skip) {
        this.docMouseService.handleDocumentClickedEvent(evt, false);
        this.docMouseService.handleClickSpellcheckerDialog();
        this.docMouseService.handleClickToWordForWordPrediction();
      }

      if (htmlEditorInst.validateClick !== null && htmlEditorInst.validateClick(evt).rebuildTxtMap) {
        htmlEditorInst.asgardDoc.onDocumentChanged();
      }
    };

    const chainClickWrapper = async (evt: MouseEvent) => {
      // console.warn('htmlEditorInst.paneIndex: ', htmlEditorInst.paneIndex);
      await this.chain.handleClickEvent(ChainSenders.HTML_DOCUMENT, null, documentId, htmlEditorInst.paneIndex, evt, clickEv, [evt]);
    };

    const keyUp = async (evt: KeyboardEvent) => {
      htmlEditorInst.asgardDoc.onDocumentChanged();
      this.docKeyBoardService.handleDocumentKeyPressed(evt);
      const command = new HTMLDocumentChangedCommand(htmlEditorInst.asgardDoc);
      await this.commandInvoker.ExecuteCommand(command);
    };

    const keyDown = async (evt: KeyboardEvent) => {
      if (isSymbolSpace(evt.code)) {
        htmlEditorInst.asgardDoc.clearReadingItem();
      }

      htmlEditorInst.asgardDoc.onDocumentChanged();
      this.docKeyBoardService.handleKeyDownSpellCheck(evt);
      this.handleDocumentChange(evt, htmlEditorInst.asgardDoc);

      if (evt.keyCode === 8 || evt.keyCode === 13 || evt.keyCode === 32 ||
        evt.keyCode >= 48 && evt.keyCode <= 90 || evt.keyCode >= 186 && evt.keyCode <= 222) {
        const cursorPosition = htmlEditorInst.asgardDoc.getCursorPostion();
        // because we used it on keydown we need atach + 1 to current offset, so when symbol appear
        // his offset will match with command offset
        // we cant used keyup for that, because offset on keyup doesnt correct in many cases
        // for example if you very fast typing
        const command = new HTMLDocumentChangedCommand(htmlEditorInst.asgardDoc, evt, cursorPosition + 1);
        await this.commandInvoker.ExecuteCommand(command);
      }
    };

    const mouseOnEv = (evt: MouseEvent) => {
      if (this.mouseIn) {
        if (this.audioMakerService.isActive()) {
          this.docMouseService.mouseInAndMoving();
        } else if (this.readingModeService.readingMode === ReadingMode.Hover) {
          this.mouseHoverText.next(evt);
        }
      }
    };

    if (htmlEditorInst.asgardDoc instanceof AsgardDocFroala) {
      const callback = (cmd: string) => {
        if (cmd === 'print') {
          this.readingService.stop();
          htmlEditorInst.asgardDoc.clearTextHighlight();
        }
      };

      htmlEditorInst.asgardDoc.addEditorEventListener('commands.before', callback);

      htmlEditorInst.asgardDoc.contentWasSet.subscribe(async x => {
        if (x) {
          await this.autosave.checkForSavedPageAndReadingPostion(documentId);
          this.spellCheckService.runSpellCheck();
        }
      });
    }

    const mouseInUpd = (evt: MouseEvent) => {
      this.mouseIn = evt.type === 'mouseenter';
    };
    const docChange = () => {
      htmlEditorInst.asgardDoc.onDocumentChanged();
    };

    const mouseDown = () => {
      this.docMouseService.mouseLeftDown();
    };
    const mouseUp = () => {
      this.docMouseService.mouseLeftUp();
    };

    const examHotkeysDetectionHandler = (e: KeyboardEvent) => {
      if (detectHotkey(e)) {
        const hotkey = getHotkeyString(e);
        const source = htmlEditorInst.asgardDoc instanceof AsgardDocHtml ? HotkeyEventSource.TextNote : HotkeyEventSource.FroalaEditor;
        this.hotkeyService.hotkeyDetected.next({ hotkey, source });
      }
    }

    const dragStart = () => {
      this.readingService.stop();
      this.instanceStorage.get(documentId)?.asgardDoc.clearTextHighlight();
      this.instanceStorage.get(documentId)?.asgardDoc.clearReadingItem();
    }

    if (htmlEditorInst.asgardDoc.editorElement) {
      htmlEditorInst.asgardDoc.editorElement.addEventListener('mousedown', mouseDown);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('mouseup', mouseUp);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('mouseenter', mouseInUpd);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('mouseleave', mouseInUpd);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('click', chainClickWrapper);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('mousemove', mouseOnEv);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('keydown', keyDown);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('keyup', keyUp);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('paste', docChange);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('cut', docChange);
      htmlEditorInst.asgardDoc.editorElement.addEventListener('dragstart', dragStart);

      // check hotkeys in Exam mode
      if (this.examService.inProgress) {
        htmlEditorInst.asgardDoc.editorElement.addEventListener('keydown', examHotkeysDetectionHandler);
      }
    }

    htmlEditorInst.editorListeners = [
      { type: 'mousedown', listener: mouseDown },
      { type: 'mouseup', listener: mouseUp },
      { type: 'mouseenter', listener: mouseInUpd },
      { type: 'mouseleave', listener: mouseInUpd },
      { type: 'click', listener: clickEv },
      { type: 'mousemove', listener: mouseOnEv },
      { type: 'keydown', listener: keyDown },
      { type: 'keyup', listener: keyUp },
      { type: 'paste', listener: docChange },
      { type: 'cut', listener: docChange },
      { type: 'keydown', listener: examHotkeysDetectionHandler },
    ];

    // window listener for resize event
    const syncFrame = () => {
      this.syncIFrame(htmlEditorInst);
    }
    window.addEventListener('resize', syncFrame);

    htmlEditorInst.windowListeners = [
      { type: 'resize', listener: syncFrame }
    ];

    if (htmlEditorInst.instSubscr && htmlEditorInst.asgardDoc.editorElement) {
      htmlEditorInst.instSubscr.add(
        fromEvent(htmlEditorInst.asgardDoc.editorElement, 'scroll')
          .pipe(throttleTime(250, asyncScheduler, { leading: false, trailing: true }))
          .subscribe(() => this.documentScrollingService.updateShowedPageRect())
      );
    }
  }

  private handleDocumentChange(evt: KeyboardEvent, doc: AsgardDocHtml): void {
    const doReadWhileType = this.settingsService.settings.readWhileTyping;
    const currEchoUnit = this.settingsService.settings.typingUnit;
    const rwtTimeout = 500;

    const key = evt.key.toLowerCase();
    const undoOrRedoHK = (key === 'z') && (evt.ctrlKey || evt.metaKey) || evt.shiftKey;
    const specKeySet = key === 'enter' || key === 'delete' || key === 'backspace' || key === 'tab' || key === ' ';
    const simplePunctKeySet = key === 'enter' || key === '.' || key === '!' || key === '?' || key === ';';

    if (undoOrRedoHK || specKeySet) {
      doc.onDocumentChanged();
    }

    if (!doReadWhileType || evt.ctrlKey || evt.metaKey || evt.altKey) {
      return;
    }

    if (matchReadingEchoWithSettings(currEchoUnit, ReadingEcho.Character) && key.length === 1) {
      if (this.rwtTimerID) {
        clearTimeout(this.rwtTimerID);
      }

      if (!specKeySet && !simplePunctKeySet) {
        this.rwtTimerID = setTimeout((() => this.readingService.readSimpleText(key)) as TimerHandler, rwtTimeout);
      }
    } else if (matchReadingEchoWithSettings(currEchoUnit, ReadingEcho.Word) && (simplePunctKeySet || key === ' ' || key === ',')) {
      const itemWord = doc.getItemBeforeCursor(ReadingUnitType.Word);

      if (!this.hasReadLastItem && itemWord !== null && itemWord.length > 0) {
        this.readingService.readSimpleText(itemWord[0].text);
        this.hasReadLastItem = true;
      }
    } else if (matchReadingEchoWithSettings(currEchoUnit, ReadingEcho.Sentence) && simplePunctKeySet) {
      const words = doc.getItemBeforeCursor(ReadingUnitType.Sentence);
      if (!this.hasReadLastItem && words !== null && words.length > 0) {
        this.readingService.readSimpleText(words.map(x => x.text).join(' '));
        this.hasReadLastItem = true;
      }
    } else {
      this.hasReadLastItem = false;
    }
  }

  private destroyAndReInitializeAllEditorInstances() {
    this.instanceStorage.forEach((instance) => {
      const froalaInstanceManager = instance.froalaInstanceManager;
      if (froalaInstanceManager) {
        // 'document changed' so a new text iterator is created
        instance.asgardDoc.onDocumentChanged();
        froalaInstanceManager.destroy();

        // need a delay between destroy() and initialize()
        // because: can not use destroy event because only when language changes 
        // does it need to be reinitialized, so not on every destroy!
        setTimeout(() => {
          froalaInstanceManager.initialize();
          // NOTE createEditorInstance() is not triggered, but onInstanceReady is!
          instance.editorAPI = froalaInstanceManager.getEditor();
        }, 100);
      }
    });
  }
}
